import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import ApiService from '../services/api';
import toast from 'react-hot-toast';
import { CONFIG } from '../config/config';
import { computePayableTotalInr, getSaleUnitPriceInr } from '../utils/pricing';

const CartContext = createContext({});

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({
    items: [],
    subtotal: 0,
    itemCount: 0,
  });
  const [loading, setLoading] = useState(false);

  const normalizeGuestCart = (guestCart) => {
    const raw = guestCart.items || [];
    const items = raw.map((it) => ({
      ...it,
      unitPriceInr:
        it.unitPriceInr != null
          ? Number(it.unitPriceInr)
          : getSaleUnitPriceInr(),
      _id:
        it._id ||
        `guest_${it.productId}_${it.size}_${it.color}_${it.name || ''}`.replace(
          /\s/g,
          ''
        ),
    }));

    const itemCount = items.reduce(
      (sum, item) => sum + (item.quantity || 0),
      0
    );
    const subtotal = computePayableTotalInr(items);
    return { ...guestCart, items, itemCount, subtotal };
  };

  // ✅ Persist guest cart
  const persistGuestCart = (guestCart) => {
    const next = normalizeGuestCart(guestCart);
    localStorage.setItem(
      CONFIG.STORAGE.GUEST_CART,
      JSON.stringify(next)
    );
    window.dispatchEvent(new Event('cartUpdated'));
    return next;
  };

  const loadCart = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const data = await ApiService.getCart();
        setCart(data.data);
      } else {
        const savedCart = localStorage.getItem(
          CONFIG.STORAGE.GUEST_CART
        );
        if (savedCart) {
          const parsed = JSON.parse(savedCart);
          setCart(normalizeGuestCart(parsed));
        }
      }
    } catch (error) {
      console.error('Error loading cart:', error);
    }
  }, []);

  // ✅ FIXED: dependency is stable now
  useEffect(() => {
    loadCart();
  }, [loadCart]);

  const addToCart = async (product, quantity = 1, size, color) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');

      if (token) {
        const data = await ApiService.addToCart({
          productId: product.id,
          quantity,
          size,
          color,
        });
        setCart(data.data);
        toast.success('Added to cart!');
      } else {
        const guestCart = { ...cart, items: [...(cart.items || [])] };
        const unitPriceInr =
          product.unitPriceInr != null
            ? Number(product.unitPriceInr)
            : getSaleUnitPriceInr();

        const existingItemIndex = guestCart.items.findIndex(
          (item) =>
            item.productId === product.id &&
            item.size === size &&
            item.color === color
        );

        if (existingItemIndex > -1) {
          guestCart.items[existingItemIndex].quantity += quantity;
          guestCart.items[existingItemIndex].unitPriceInr = unitPriceInr;
        } else {
          guestCart.items.push({
            _id: `guest_${Date.now()}_${Math.random()
              .toString(36)
              .slice(2, 9)}`,
            productId: product.id,
            name: product.name,
            unitPriceInr,
            quantity,
            size,
            color,
            image: product.images?.[0] || product.image,
          });
        }

        const next = persistGuestCart(guestCart);
        setCart(next);
        toast.success('Added to cart!');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to add to cart');
      console.error('Error adding to cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (itemId, quantity) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');

      if (token) {
        const data = await ApiService.updateCartItem(itemId, quantity);
        setCart(data.data);
        toast.success('Cart updated');
      } else {
        const guestCart = { ...cart, items: [...(cart.items || [])] };
        const itemIndex = guestCart.items.findIndex(
          (item) => item._id === itemId
        );

        if (itemIndex > -1) {
          guestCart.items[itemIndex].quantity = quantity;
          const next = persistGuestCart(guestCart);
          setCart(next);
          toast.success('Cart updated');
        }
      }
    } catch (error) {
      toast.error(error.message || 'Failed to update cart');
      console.error('Error updating cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeItem = async (itemId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');

      if (token) {
        const data = await ApiService.removeCartItem(itemId);
        setCart(data.data);
        toast.success('Item removed from cart');
      } else {
        const guestCart = {
          ...cart,
          items: (cart.items || []).filter((item) => item._id !== itemId),
        };
        const next = persistGuestCart(guestCart);
        setCart(next);
        toast.success('Item removed from cart');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to remove item');
      console.error('Error removing item:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearCart = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');

      if (token) {
        await ApiService.clearCart();
      }

      setCart({ items: [], subtotal: 0, itemCount: 0 });
      localStorage.removeItem(CONFIG.STORAGE.GUEST_CART);
      window.dispatchEvent(new Event('cartUpdated'));
      toast.success('Cart cleared');
    } catch (error) {
      toast.error(error.message || 'Failed to clear cart');
      console.error('Error clearing cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const mergeCart = async () => {
    const guestCart = localStorage.getItem(
      CONFIG.STORAGE.GUEST_CART
    );

    if (guestCart && localStorage.getItem('token')) {
      try {
        await ApiService.mergeCart(JSON.parse(guestCart));
        localStorage.removeItem(CONFIG.STORAGE.GUEST_CART);
        await loadCart();
      } catch (error) {
        console.error('Error merging cart:', error);
      }
    }
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        loading,
        addToCart,
        updateQuantity,
        removeItem,
        clearCart,
        mergeCart,
        loadCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};